import { ReactNode, Suspense, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { sanitizeImageUrl } from "@/lib/sanitize";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AppSidebar, SidebarProvider, useSidebar, MobileMenuTrigger } from "./AppSidebar";
import { NetworkStatusBanner } from "./NetworkStatusBanner";
import { NotificationCenter } from "./NotificationCenter";
import { ChangelogEntryPointButton } from "@/components/changelog/ChangelogModal";
import { reportAppLayoutMounted, reportAppLayoutChangelogEntryPointMounted, reportChromeFreeKeyboardScopeMounted } from "./AppLayoutMountSignal";
import { useNotificationSubscription } from "@/hooks/useNotifications";
import { ArrowRightLeft, Bell, Building2, Check, ChevronsUpDown, CircleHelp, Plus, Search, Settings, Shield, User, LogOut, Sparkles, BookOpen, MessageCircle, MessagesSquare, Mail, ExternalLink, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { useActionTelemetry } from "@/hooks/useActionTelemetry";
import { useTenantContext } from "@/hooks/useTenantContext";
import { useFeatureGate } from "@/hooks/useTierGate";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useAccessContext } from "@/hooks/useAccessContext";
import { usePermissions } from "@/hooks/usePermissions";
import { QUICK_ADD_MODULES } from "@/lib/quick-add-permissions";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getModuleAccent as getModuleIdentityAccent } from "@/lib/module-identity";
import { moduleAccentScope, type ModuleAccent } from "@/lib/module-accents";
import { getModuleForRoute } from "./moduleForRoute";
import { ModuleScopeContext } from "@/components/ui/module-scope";
import { useUiPreferences } from "@/hooks/useUiPreferences";
import { useUiUplift } from "@/hooks/useUiUplift";
import { PageHeader, type PageHeaderProps } from "@/components/shared/PageHeader";
import { EnhancedGlobalSearchPalette } from "@/components/search";
import { useGlobalSearchPalette } from "@/hooks/useGlobalSearchPalette";
import { useGlobalHotkeys } from "@/hooks/useGlobalHotkeys";
import { SolomonPanel, useSolomonContext } from "@/components/solomon";
import { normalizeSolomonAction } from "@/components/solomon/solomon-actions";
import { AppLayoutTitleContext } from "@/components/layout/AppLayoutTitleContext";
import { TopbarBreadcrumbSlotContext, TopbarBreadcrumbClaimContext, TOPBAR_BREADCRUMB_BREAKPOINT } from "./topbarBreadcrumbSlot";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAppRoutePolicy } from "@/components/layout/AppRoutePolicyContext";
import { SharedSvgDefs } from "@/components/svg/SharedSvgDefs";
import { OfflineIndicator } from "@/components/common/OfflineIndicator";
import { SkipNav } from "@/lib/shared-ui";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { InstallPrompt } from "@/components/common/InstallPrompt";
import { setStatusBarForModule } from "@/lib/splash";
import { Keyboard } from "@capacitor/keyboard";
import { App as CapApp } from "@capacitor/app";
import { platform } from "@/lib/platform";
import { useQueryClient } from "@tanstack/react-query";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";

// Dialog imports for Quick Add
import type { PersonFormDefaults } from "@/components/people/PersonFormDialog";
import {
  QuickAddProvider,
  type QuickAddContextValue,
  type QuickAddRequestDefaults,
} from "@/components/layout/QuickAddContext";
import { QuickAddPanel } from "@/components/layout/QuickAddPanel";
import { Dialog, DialogContent, DialogGenieContext, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useUpdateTaskStatus } from "@/hooks/useTasks";
import { useUpdateRequestStatus, type RequestStatus } from "@/hooks/useRequests";
import { useUpdateContributionStatus } from "@/hooks/useContributions";
import { lazyWithRetry } from "@/lib/lazy-with-retry";

const ServiceFormDialog = lazyWithRetry(() =>
  import("@/components/services/ServiceFormDialog").then((module) => ({ default: module.ServiceFormDialog }))
);

const PersonFormDialog = lazyWithRetry(() =>
  import("@/components/people/PersonFormDialog").then((module) => ({ default: module.PersonFormDialog }))
);

const QuickContributionEntry = lazyWithRetry(() =>
  import("@/components/contributions/QuickContributionEntry").then((module) => ({ default: module.QuickContributionEntry }))
);

const GeneralRequestDialog = lazyWithRetry(() =>
  import("@/components/requests/CreateRequestDialog").then((module) => ({ default: module.GeneralRequestDialog }))
);

const EventFormDialog = lazyWithRetry(() =>
  import("@/components/events/EventFormDialog").then((module) => ({ default: module.EventFormDialog }))
);

const ProjectFormDialog = lazyWithRetry(() =>
  import("@/components/projects/ProjectFormDialog").then((module) => ({ default: module.ProjectFormDialog }))
);

const ComposeMessageDialog = lazyWithRetry(() =>
  import("@/components/messaging/ComposeMessageDialog").then((module) => ({ default: module.ComposeMessageDialog }))
);

export interface AppLayoutProps {
  children: ReactNode;
  title?: string;
  titleIsShell?: boolean;
  subtitle?: string;
  demoteQuickAdd?: boolean;
  showSidebar?: boolean;
  /**
   * T4 tasks retain the shared providers and main landmark while their child owns
   * the task bar, title and content spacing. Chrome-free omits app navigation and
   * shell shortcuts; explicit child Quick Add / Solomon actions remain available.
   * @example <AppLayout mode="chrome-free"><GuidedTaskContent /></AppLayout>
   * Decision: https://github.com/Glorify-LLC/forgehq-0358a7c6/issues/4888#issuecomment-5823654895
   */
  mode?: "standard" | "chrome-free";
  hideTitle?: boolean;
  showTopBarQuickAdd?: boolean;
  titleActions?: PageHeaderProps["actions"];
}

function AppLayoutContent({
  children,
  title,
  titleIsShell,
  subtitle,
  demoteQuickAdd,
  showSidebar = true,
  mode = "standard",
  hideTitle,
  showTopBarQuickAdd,
  titleActions,
}: AppLayoutProps) {
  const chromeFree = mode === "chrome-free";
  const renderSidebar = showSidebar && !chromeFree;
  const { user, signOut } = useAuth();
  const { roles, isStaff } = useUserRoles();
  // Single source of truth for the platform-admin gate — matches the
  // check in `AdminRoute`, so the menu entry can't be visible without
  // the route also granting access.
  const { data: accessContext } = useAccessContext();
  const isPlatformAdmin = accessContext?.capabilities?.isPlatformAdmin ?? false;
  const { can } = usePermissions();
  // True when the role can create at least one quick-add entity. Used to hide
  // the Quick Add entry points entirely for view-only roles instead of opening
  // an empty panel.
  const canQuickAdd = QUICK_ADD_MODULES.some((module) => can(module, "add"));
  const { isCollapsed, isMobile, setIsMobileOpen } = useSidebar();
  const [breadcrumbHost, setBreadcrumbHost] = useState<HTMLDivElement | null>(null);
  const [breadcrumbClaims, setBreadcrumbClaims] = useState<Set<string>>(() => new Set());
  const claimBreadcrumbs = useCallback((id: string, mounted: boolean) => {
    setBreadcrumbClaims((claims) => {
      if (claims.has(id) === mounted) return claims;
      const next = new Set(claims);
      if (mounted) next.add(id); else next.delete(id);
      return next;
    });
  }, []);
  const belowBreadcrumbTopbar = useIsMobile(TOPBAR_BREADCRUMB_BREAKPOINT);
  const { organization, organizations, switchOrganization } = useOrganization();
  const { overrideContext } = useTenantContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { open: searchOpen, setOpen: setSearchOpen, openPalette } = useGlobalSearchPalette();
  const { trackAction } = useActionTelemetry();
  useGlobalHotkeys(!chromeFree);
  // This lifecycle belongs to the shell, independently of whether its visual bell exists.
  const notificationConnection = useNotificationSubscription({ showDesktopNotifications: true });
  const {
    unreadCount,
    isOpen: solomonOpen,
    setIsOpen: setSolomonOpen,
    registerActionHandler,
  } = useSolomonContext();
  // Solomon AI is a Flourish-tier feature — hide its entry points for lower tiers.
  const { hasFeature: solomonEnabled } = useFeatureGate("solomon_ai");
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [solomonDraft, setSolomonDraft] = useState<string | null>(null);
  const avatarUrl = (user?.user_metadata?.avatar_url as string | undefined) ??
    (user?.user_metadata?.picture as string | undefined);
  const userEmail = user?.email ?? "";
  const userInitials = userEmail
    .split("@")[0]
    .split(".")
    .map((part) => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase() || "U";

  // Quick Add panel + dialog states
  const [quickAddPanelOpen, setQuickAddPanelOpen] = useState(false);
  const headerActionsTrigger = useRef<HTMLButtonElement>(null);
  const topBarQuickAddTrigger = useRef<HTMLButtonElement>(null);
  const quickAddMenuReturnFocus = useRef(false);
  const pendingHeaderAction = useRef<{ open: () => void; scope: string } | null>(null);
  const [personDialogOpen, setPersonDialogOpen] = useState(false);
  const [personDialogLoaded, setPersonDialogLoaded] = useState(false);
  const [personDialogDefaults, setPersonDialogDefaults] = useState<PersonFormDefaults | undefined>(undefined);
  const [contributionDialogOpen, setContributionDialogOpen] = useState(false);
  const [contributionDialogLoaded, setContributionDialogLoaded] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestDialogLoaded, setRequestDialogLoaded] = useState(false);
  const [requestDefaults, setRequestDefaults] = useState<QuickAddRequestDefaults>({});
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [eventDialogLoaded, setEventDialogLoaded] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [serviceDialogLoaded, setServiceDialogLoaded] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageDialogLoaded, setMessageDialogLoaded] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [projectDialogLoaded, setProjectDialogLoaded] = useState(false);
  const [genieOrigin, setGenieOrigin] = useState<{ x: number; y: number } | null>(null);
  const { mutate: updateTaskStatus } = useUpdateTaskStatus();
  const { updateStatus: updateRequestStatus } = useUpdateRequestStatus();
  const { mutate: updateContributionStatus } = useUpdateContributionStatus();
  const latestUpdateTaskStatus = useRef<typeof updateTaskStatus>(() => {});
  const latestUpdateRequestStatus = useRef<
    (id: string, status: RequestStatus, previousStatus: RequestStatus) => Promise<void> | void
  >(() => {});
  const latestUpdateContributionStatus = useRef<typeof updateContributionStatus>(() => {});
  const latestOpenMessageDialog = useRef<() => void>(() => {});
  const latestNavigate = useRef<ReturnType<typeof useNavigate>>(() => {});
  useEffect(() => {
    const handleNewContribution = () => {
      setContributionDialogLoaded(true);
      setContributionDialogOpen(true);
    };
    const handleOpenPersonLookup = () => {
      if (chromeFree) return;
      navigate("/people/all", { state: { focusPeopleSearch: true } });
    };
    const handleOpenShortcutsHelp = () => {
      if (!chromeFree) setShortcutsOpen(true);
    };
    const handleGoPeople = () => {
      if (chromeFree) return;
      navigate("/people/all", { state: { focusPeopleSearch: true } });
    };
    const handleOpenSolomon = (event: Event) => {
      const detail = (event as CustomEvent<{ prompt?: string }>).detail;
      if (detail?.prompt) {
        setSolomonDraft(detail.prompt);
      }
      setSolomonOpen(true);
    };
    // Handler for quick-add action from search palette. Logged inline (not via
    // openQuickAddPanel) to avoid churning this effect's deps; source
    // distinguishes it from the top-bar / mobile openers.
    const handleOpenQuickAdd = () => {
      if (chromeFree) return;
      trackAction({ action: "quick_add_open", source: "app_shell", metadata: { surface: "search_palette" } });
      setQuickAddPanelOpen(true);
    };

    window.addEventListener("new-contribution", handleNewContribution);
    window.addEventListener("open-person-lookup", handleOpenPersonLookup);
    window.addEventListener("open-shortcuts-help", handleOpenShortcutsHelp);
    window.addEventListener("go-people", handleGoPeople);
    window.addEventListener("open-solomon", handleOpenSolomon);
    window.addEventListener("open-quick-add", handleOpenQuickAdd);

    return () => {
      window.removeEventListener("new-contribution", handleNewContribution);
      window.removeEventListener("open-person-lookup", handleOpenPersonLookup);
      window.removeEventListener("open-shortcuts-help", handleOpenShortcutsHelp);
      window.removeEventListener("go-people", handleGoPeople);
      window.removeEventListener("open-solomon", handleOpenSolomon);
      window.removeEventListener("open-quick-add", handleOpenQuickAdd);
    };
  }, [chromeFree, navigate, setSolomonOpen, trackAction]);

  // Reports whether this AppLayout instance is currently mounted, so
  // sibling-tree components that can't rely on React context (e.g.
  // ChangelogModal, mounted once at the App.tsx route-wrapper level, outside
  // AppLayout's own subtree) can detect whether the open-solomon listener
  // above and <SolomonPanel> below actually exist to receive an event right
  // now, instead of hand-maintaining a list of routes known to lack
  // AppLayout — which can only ever cover routes already discovered (see
  // AppLayoutMountSignal.ts for why this is a queryable module-level signal,
  // not a plain fire-and-forget window event). Every route change that swaps
  // AppLayout out for a different page shell re-fires this (unmount then
  // remount), which is expected and harmless.
  useEffect(() => {
    reportAppLayoutMounted(true, false);
    return () => {
      reportAppLayoutMounted(false, false);
    };
  }, []);

  // Presence and the header's reopen capability are distinct: the task still
  // has AppLayout/Solomon, but no What's New entry point for an automatic popup.
  useEffect(() => {
    if (!chromeFree) return;
    reportChromeFreeKeyboardScopeMounted(true);
    return () => reportChromeFreeKeyboardScopeMounted(false);
  }, [chromeFree]);

  useEffect(() => {
    if (chromeFree) return;
    reportAppLayoutChangelogEntryPointMounted(true);
    return () => reportAppLayoutChangelogEntryPointMounted(false);
  }, [chromeFree]);

  const handleSignOut = async () => {
    // Fire before signOut() so the JWT is still valid for log_platform_event.
    // Fire-and-forget (not awaited) so it never delays logout — may race the
    // session teardown + /auth redirect and drop, so sign_out can undercount.
    trackAction({ action: "sign_out", source: "app_shell" });
    await signOut();
    navigate("/auth");
  };

  const openPersonDialog = useCallback((defaults?: PersonFormDefaults) => {
    setPersonDialogDefaults(defaults);
    setPersonDialogLoaded(true);
    setPersonDialogOpen(true);
  }, []);

  const openContributionDialog = useCallback(() => {
    setContributionDialogLoaded(true);
    setContributionDialogOpen(true);
  }, []);

  const openRequestDialog = useCallback((defaults: QuickAddRequestDefaults = {}) => {
    setRequestDefaults(defaults);
    setRequestDialogLoaded(true);
    setRequestDialogOpen(true);
  }, []);

  const openEventDialog = useCallback(() => {
    setEventDialogLoaded(true);
    setEventDialogOpen(true);
  }, []);

  const openServiceDialog = useCallback(() => {
    setServiceDialogLoaded(true);
    setServiceDialogOpen(true);
  }, []);

  const openProjectDialog = useCallback(() => {
    setProjectDialogLoaded(true);
    setProjectDialogOpen(true);
  }, []);

  const openMessageDialog = useCallback(() => {
    setMessageDialogLoaded(true);
    setMessageDialogOpen(true);
  }, []);

  // Shell-open telemetry helpers: each shell surface is reached from multiple
  // entry points, so log once here and swap the helper in at every call site to
  // keep the event_type/source identical and avoid double-emitting. trackAction
  // (not trackDialog) so each verb gets its own event_type + sampleRate.
  const openQuickAddPanel = useCallback((surface: string) => {
    trackAction({ action: "quick_add_open", source: "app_shell", metadata: { surface } });
    setQuickAddPanelOpen(true);
  }, [trackAction]);
  const openCommandPalette = useCallback((surface: string) => {
    trackAction({ action: "command_palette_open", source: "app_shell", metadata: { surface } });
    openPalette();
  }, [trackAction, openPalette]);

  useEffect(() => {
    latestUpdateTaskStatus.current = updateTaskStatus;
    latestUpdateRequestStatus.current = updateRequestStatus;
    latestUpdateContributionStatus.current = updateContributionStatus;
    latestOpenMessageDialog.current = openMessageDialog;
    latestNavigate.current = navigate;
  }, [navigate, openMessageDialog, updateContributionStatus, updateRequestStatus, updateTaskStatus]);

  const captureGenieOrigin = useCallback(() => {
    const btn = document.querySelector("[data-quick-add-trigger]") as HTMLElement | null;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setGenieOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
    }
  }, []);

  const clearGenieOrigin = useCallback(() => {
    setGenieOrigin(null);
  }, []);

  const handleSolomonAction = useCallback(
    (action: string, hintId: string, metadata?: Record<string, unknown>) => {
      const normalizedAction = normalizeSolomonAction(action);
      if (!normalizedAction) return;

      if (normalizedAction === "send-message") {
        latestOpenMessageDialog.current();
        return;
      }

      if (normalizedAction === "create-task") {
        latestNavigate.current("/tasks?create=true", {
          state: {
            source: "solomon",
            hintId,
            metadata,
          },
        });
        return;
      }

      if (normalizedAction === "update-status") {
        const payload = metadata as
          | {
              entity?: "task" | "request" | "contribution";
              id?: string;
              status?: string;
              previousStatus?: string;
            }
          | undefined;

        if (!payload?.entity || !payload?.id || !payload?.status) {
          toast.error("Status update is missing details.");
          return;
        }

        if (payload.entity === "task") {
          const nextStatus = payload.status as "todo" | "in_progress" | "done";
          // Routed through update_own_task_status (#4999) rather than a raw
          // table update — completed_at is stamped/cleared server-side to
          // match, so Today's "tasks cleared" pulse stat still sees
          // completions made from here.
          latestUpdateTaskStatus.current({ id: payload.id, status: nextStatus });
          return;
        }

        if (payload.entity === "request") {
          if (!payload.previousStatus) {
            toast.error("Request status update needs the previous status.");
            return;
          }
          latestUpdateRequestStatus.current(
            payload.id,
            payload.status as RequestStatus,
            payload.previousStatus as RequestStatus
          );
          return;
        }

        latestUpdateContributionStatus.current({
          id: payload.id,
          status: payload.status,
        });
      }
    },
    []
  );

  useEffect(() => {
    const unsubscribe = registerActionHandler(handleSolomonAction);
    return unsubscribe;
  }, [registerActionHandler, handleSolomonAction]);

  const handlePersonDialogChange = useCallback((nextOpen: boolean) => {
    setPersonDialogOpen(nextOpen);
    if (!nextOpen) {
      setPersonDialogDefaults(undefined);
    }
  }, []);

  const quickAddContextValue = useMemo<QuickAddContextValue>(
    () => ({
      openPersonDialog,
      openContributionDialog,
      openRequestDialog,
      openEventDialog,
      openServiceDialog,
      openProjectDialog,
      openMessageDialog,
      captureGenieOrigin,
    }),
    [
      captureGenieOrigin,
      openContributionDialog,
      openEventDialog,
      openMessageDialog,
      openPersonDialog,
      openProjectDialog,
      openRequestDialog,
      openServiceDialog,
    ],
  );

  const routePolicy = useAppRoutePolicy();
  const { moduleIdentity, tableDensity } = useUiPreferences();
  // UI uplift: "next" renders the new chassis, "classic" today's (src/lib/ui-uplift.ts).
  const uiVariant = useUiUplift();
  const moduleAccent = getModuleIdentityAccent(location.pathname);
  const moduleAccentValue = moduleIdentity === "subtle" ? moduleAccent : "0 0% 0% / 0";
  // The page's module as its navigation names it (#7912), so the body, the sub-nav bar and the
  // sidebar agree; classic keeps the keyword classification it always had.
  const richAccent: ModuleAccent = getModuleForRoute(location.pathname, uiVariant);
  const moduleScope = useMemo(() => moduleAccentScope(richAccent), [richAccent]);

  // Update native status bar color when the module accent changes
  useEffect(() => {
    if (richAccent.color) {
      setStatusBarForModule(richAccent.color);
    }
  }, [richAccent.color]);

  // Keyboard handling — set CSS variable for keyboard-aware layouts (Phase 2.6)
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!platform.isNative) return;

    const showListener = Keyboard.addListener('keyboardWillShow', (info) => {
      document.body.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
    });
    const hideListener = Keyboard.addListener('keyboardWillHide', () => {
      document.body.style.setProperty('--keyboard-height', '0px');
    });

    return () => {
      showListener.then(l => l.remove());
      hideListener.then(l => l.remove());
    };
  }, []);

  // Background app refresh — invalidate critical queries on foreground resume (Phase 3.4)
  useEffect(() => {
    if (!platform.isNative) return;

    const listener = CapApp.addListener('appStateChange', (state) => {
      if (state.isActive) {
        queryClient.invalidateQueries({ queryKey: ['messages'] });
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      }
    });

    return () => { listener.then(l => l.remove()); };
  }, [queryClient]);

  const hasLayoutHeader = Boolean(title) || Boolean(titleActions);
  const hideLayoutTitle = hideTitle || !title;
  const routePolicyShowTopBarQuickAdd = routePolicy.suppressTopBarQuickAdd
    ? false
    : routePolicy.showTopBarQuickAdd ?? true;
  const resolvedShowTopBarQuickAdd = showTopBarQuickAdd ?? routePolicyShowTopBarQuickAdd;
  const topbarBreadcrumbsEnabled = uiVariant === "next" && renderSidebar && !belowBreadcrumbTopbar && !(isMobile && platform.isNative);
  const compactBreadcrumbChrome = topbarBreadcrumbsEnabled && breadcrumbClaims.size > 0;
  const phoneHeaderOverflow = uiVariant === "next" && renderSidebar && isMobile && !platform.isNative;
  const headerActionScope = JSON.stringify([organization?.id, user?.id, location.key, uiVariant, isMobile, renderSidebar, chromeFree, resolvedShowTopBarQuickAdd, canQuickAdd]);
  const currentHeaderActionScope = useRef(headerActionScope);
  currentHeaderActionScope.current = headerActionScope;
  useEffect(() => () => { pendingHeaderAction.current = null; }, []);

  return (
    <QuickAddProvider value={quickAddContextValue}>
      {/* Overlays are portaled out of the scoped root below; they read the scope from here (#7912).
          Only under the next chassis: a classic page's overlays are unchanged. */}
      <ModuleScopeContext.Provider value={uiVariant === "next" ? moduleScope : null}>
      <TopbarBreadcrumbSlotContext.Provider value={topbarBreadcrumbsEnabled ? breadcrumbHost : null}>
      <TopbarBreadcrumbClaimContext.Provider value={topbarBreadcrumbsEnabled ? claimBreadcrumbs : null}>
      {/* First focusable element: lets keyboard/SR users bypass the sidebar +
          top bar and jump to <main id="main-content"> (2.4.1 Bypass Blocks). */}
      <SkipNav />
      <OfflineIndicator />
      <SharedSvgDefs />
      <div
        data-ui={uiVariant}
        data-chassis={showSidebar ? "admin" : "portal"}
        className={cn(
          "flex h-screen flex-col overflow-x-hidden bg-background",
          tableDensity === "compact" ? "density-compact" : "density-comfortable"
        )}
        // The route's module accent, light and dark; index.css picks the pair for the theme (#7915).
        data-module-scope={moduleScope["data-module-scope"]}
        style={{ "--module-accent": moduleAccentValue, ...moduleScope.style } as CSSProperties}
      >
        {renderSidebar && (
          <div className="app-layout-chrome print-hidden">
            <AppSidebar />
          </div>
        )}
        
        {/* Main Content */}
        <div
          className={cn(
            "flex min-h-0 flex-1 flex-col transition-all duration-300 ease-out print:pl-0",
            renderSidebar
              ? (isMobile ? "pl-0" : (isCollapsed ? "pl-20" : "pl-64"))
              : "pl-0",
          )}
        >
            {/* Top Header — slim on mobile native (logo + Solomon + Notifications only) */}
            {!chromeFree && <header
              className={cn(
                "app-layout-chrome print-hidden sticky top-0 z-30 flex items-center justify-between border-b border-border/60 px-3 md:px-6 gap-4",
                compactBreadcrumbChrome && "md:px-3 lg:px-6 md:gap-2 lg:gap-4",
                phoneHeaderOverflow && "gap-2",
                !(isMobile && platform.isNative) && "h-14 md:h-[var(--layout-topbar-height)]",
                isMobile && platform.isNative
                  ? "bg-background"
                  : "bg-background"
              )}
              style={{
                paddingTop: isMobile ? "var(--safe-area-inset-top)" : undefined,
                paddingLeft: isMobile ? "max(0.75rem, var(--safe-area-inset-left))" : undefined,
                paddingRight: isMobile ? "max(0.75rem, var(--safe-area-inset-right))" : undefined,
                minHeight: (isMobile && platform.isNative) ? "calc(3.5rem + var(--safe-area-inset-top))" : undefined,
              }}
            >
          <div className={cn("flex items-center gap-3", compactBreadcrumbChrome || phoneHeaderOverflow ? "min-w-0 flex-1 gap-2 lg:gap-3" : "shrink-0")}>
            {/* Mobile menu trigger (hamburger). Shown on every mobile surface —
                web, PWA, and the native iOS/Android shell. Previously the native
                build hid it and relied solely on the bottom-nav "More" tab to open
                the drawer, which left native users with no visible, reliable way
                into the navigation. MobileMenuTrigger no-ops on desktop. */}
            {renderSidebar && <MobileMenuTrigger />}

            {/* Org switcher — inline with top bar, full-width logo */}
            {organizations.length > 1 ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className={cn("flex items-center gap-2 rounded-lg px-1.5 py-1 text-left transition-colors hover:bg-accent group", compactBreadcrumbChrome ? "min-w-11 min-h-11 lg:min-w-6 shrink max-w-[64px] lg:max-w-[128px] xl:max-w-[220px]" : phoneHeaderOverflow ? "min-w-11 min-h-11 shrink max-w-full" : "max-w-[220px]")} title={compactBreadcrumbChrome || phoneHeaderOverflow ? (overrideContext?.organizationName ?? organization?.name ?? "Select org") : undefined}>
                    {organization?.logo_url && sanitizeImageUrl(organization.logo_url) ? (
                      <img src={sanitizeImageUrl(organization.logo_url)} alt={organization.name ?? ""} className={cn("h-8 max-w-[140px] object-contain", (compactBreadcrumbChrome || phoneHeaderOverflow) && "min-w-0 max-w-full")} />
                    ) : (
                      <span className="text-sm font-semibold truncate">{overrideContext?.organizationName ?? organization?.name ?? "Select org"}</span>
                    )}
                    <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/60 group-hover:text-muted-foreground flex-shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[260px]">
                  <DropdownMenuLabel className="text-xs text-muted-foreground">Switch organization</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {organizations.map((org) => (
                    <DropdownMenuItem key={org.id} onClick={() => { trackAction({ action: "org_switch", category: "organization", source: "app_shell", metadata: { toOrgId: org.id, fromOrgId: organization?.id ?? null } }); switchOrganization(org.id); }} className="flex items-center gap-3 cursor-pointer py-2">
                      {org.logo_url && sanitizeImageUrl(org.logo_url) ? (
                        <img src={sanitizeImageUrl(org.logo_url)} alt={org.name} className="h-6 max-w-[100px] object-contain flex-shrink-0" />
                      ) : (
                        <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      )}
                      <span className="text-sm font-medium truncate flex-1">{org.name}</span>
                      {organization?.id === org.id && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : organization ? (
              <div className={cn("flex items-center px-1.5 py-1", compactBreadcrumbChrome && "min-w-0 shrink max-w-[64px] lg:max-w-[128px] xl:max-w-[160px]", phoneHeaderOverflow && "min-w-0 shrink max-w-full")}>
                {organization.logo_url && sanitizeImageUrl(organization.logo_url) ? (
                  <img src={sanitizeImageUrl(organization.logo_url)} alt={organization.name ?? ""} className={cn("h-8 max-w-[160px] object-contain", (compactBreadcrumbChrome || phoneHeaderOverflow) && "min-w-0 max-w-full")} />
                ) : (
                  <span className="text-sm font-semibold truncate max-w-[160px]">{overrideContext?.organizationName ?? organization.name}</span>
                )}
              </div>
            ) : null}
            {topbarBreadcrumbsEnabled && <div ref={setBreadcrumbHost} className="hidden min-w-0 flex-1 md:flex" data-topbar-breadcrumb-host />}
          </div>
          
          <div className={cn("flex items-center justify-end gap-2 md:gap-2.5 lg:gap-3", compactBreadcrumbChrome || phoneHeaderOverflow ? "shrink-0 gap-1 lg:gap-3" : "min-w-0 flex-1")}>
              {/* Quick Add Button — hidden on mobile native (bottom nav FAB replaces it)
                  and for roles that can't create anything. */}
              {resolvedShowTopBarQuickAdd && canQuickAdd && !phoneHeaderOverflow && !(isMobile && platform.isNative) ? (
                <Button
                  size="default"
                  variant={demoteQuickAdd ? "secondary" : "primary"}
                  aria-label="Open Quick Add menu"
                  title="Open Quick Add menu"
                  ref={topBarQuickAddTrigger}
                  className={cn("h-10 gap-1.5 rounded-xl px-3.5 text-meta font-semibold tracking-tight", !demoteQuickAdd && "btn-forge-cta", compactBreadcrumbChrome && "w-11 px-0 lg:w-auto lg:px-3.5")}
                  data-quick-add-trigger
                  onClick={() => openQuickAddPanel("top_bar")}
                >
                  <Plus className="h-4 w-4" />
                  <span className={compactBreadcrumbChrome ? "hidden lg:inline" : "inline"}>Quick Add</span>
                </Button>
              ) : null}

            {/* Search shell - opens ⌘K drawer */}
            <Button
              variant="ghost"
              className={cn("relative hidden h-10 min-w-[160px] max-w-[34rem] items-center justify-start rounded-xl border border-[var(--forge-border)] bg-[var(--forge-surface-raised)] px-3 text-left text-muted-foreground shadow-sm transition-all duration-200 hover:border-primary hover:bg-[var(--forge-surface-raised)] hover:shadow-[0_0_12px_rgba(232,160,32,0.2)]", topbarBreadcrumbsEnabled ? "xl:flex xl:w-40" : "flex-1 md:flex")}
              aria-label="Open global search"
              title="Search"
              onClick={() => openCommandPalette("top_bar_search")}
            >
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-60" />
              <span className="truncate text-meta">Search across Forge...</span>
              <kbd className="ml-auto inline-flex h-5 select-none items-center gap-1 rounded-md border border-[var(--forge-border-subtle)] bg-background/90 px-1.5 font-mono text-[11px] font-medium text-[var(--forge-text-muted)]">
                ⌘K
              </kbd>
            </Button>
            
            {/* Search icon on mobile — hidden on native (bottom nav has search) */}
            {!(isMobile && platform.isNative) && (
              <Button
                variant="ghost"
                size="icon"
                className={topbarBreadcrumbsEnabled ? "xl:hidden" : "md:hidden"}
                onClick={() => openCommandPalette(topbarBreadcrumbsEnabled ? "top_bar_search_icon" : "mobile_search_icon")}
                aria-label="Open search"
                title="Search"
              >
                <Search className="h-4 w-4" />
              </Button>
            )}

            {/*
             * Enhanced Global Search Palette — single ⌘K palette for
             * the whole app. Triggered by the "Search across Forge…"
             * button above (line ~501) AND by the global hotkey defined
             * in useGlobalHotkeys.ts.
             *
             * Previously a second `<CommandPalette />` was mounted right
             * here. It rendered its own trigger button ("Quick search…")
             * AND its own ⌘K handler — producing two visible search
             * bars in the top bar AND a hotkey-handler race where both
             * palettes opened on a single keypress. Removed; the
             * EnhancedGlobalSearchPalette is the single source of truth.
             *
             * The retired palette's hardcoded `COMMAND_ITEMS` (Add Person,
             * Record Gift, etc.) overlap entirely with the Quick Add
             * panel (top-bar button) and Solomon's `executeAction`.
             */}
            <EnhancedGlobalSearchPalette open={searchOpen} onOpenChange={setSearchOpen} />

            <div className={cn("items-center gap-1", isMobile && platform.isNative ? "flex" : "hidden lg:flex")}>
              {/* Solomon AI Assistant — Flourish tier only */}
              {solomonEnabled && <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => {
                  setSolomonDraft(null);
                  setSolomonOpen(true);
                }}
                title={
                  unreadCount > 0
                    ? `Ask Solomon — ${unreadCount} new AI ${unreadCount === 1 ? "insight" : "insights"} (click to view)`
                    : "Ask Solomon"
                }
                aria-label={
                  unreadCount > 0
                    ? `Ask Solomon, ${unreadCount} new AI ${unreadCount === 1 ? "insight" : "insights"}`
                    : "Ask Solomon"
                }
              >
                <Sparkles className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Button>}

              {/* Help & Support Dropdown — hidden on native mobile (accessible via More > sidebar) */}
              {!(isMobile && platform.isNative) && <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" title="Help & Support" aria-label="Help & Support">
                    <CircleHelp className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Help & Support</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem asChild>
                      <a href="https://forgehq.io/docs" target="_blank" rel="noopener noreferrer" className="flex items-center">
                        <BookOpen className="mr-2 h-4 w-4" />
                        Knowledge Base
                        <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a href="https://community.forgehq.io" target="_blank" rel="noopener noreferrer" className="flex items-center">
                        <MessagesSquare className="mr-2 h-4 w-4" />
                        Community Forum
                        <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                      </a>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem onClick={() => { trackAction({ action: "support_ticket_open", source: "app_shell", metadata: { origin: "help_dropdown" } }); navigate("/support/ticket"); }}>
                      <MessageCircle className="mr-2 h-4 w-4" />
                      Submit Support Ticket
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a href="mailto:support@forgehq.io" className="flex items-center">
                        <Mail className="mr-2 h-4 w-4" />
                        Email Support
                      </a>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  {solomonEnabled && <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent("open-search-with-query", { detail: { query: "?" } }));
                    }}
                    className="text-primary"
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    Ask Solomon AI
                  </DropdownMenuItem>
                  </>}
                </DropdownMenuContent>
              </DropdownMenu>}
            </div>

            {!phoneHeaderOverflow && <ChangelogEntryPointButton />}

            {/* Notification Center remains visible across breakpoints to preserve access + live subscriptions */}
            <NotificationCenter
              realtimeConnection={notificationConnection}
              onOpenChange={(open) => {
                if (open) trackAction({ action: "notification_center_open", source: "app_shell" });
              }}
            />

            {isMobile && !platform.isNative && (
              <DropdownMenu onOpenChange={(open) => { if (open) pendingHeaderAction.current = null; }}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    ref={headerActionsTrigger}
                    aria-label="Header actions"
                    title="More actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-56"
                  onCloseAutoFocus={(event) => {
                    const action = pendingHeaderAction.current;
                    if (!action) return;
                    pendingHeaderAction.current = null;
                    const trigger = headerActionsTrigger.current;
                    if (currentHeaderActionScope.current !== action.scope || !trigger?.isConnected || trigger.disabled || trigger.getAttribute("aria-disabled") === "true") return;
                    const rect = trigger.getBoundingClientRect();
                    if (!rect.width || !rect.height || getComputedStyle(trigger).visibility !== "visible") return;
                    // Close the menu and focus its persistent trigger first. The
                    // new dialog captures that return target instead of a removed
                    // menu item, and menu autofocus cannot steal the dialog focus.
                    event.preventDefault();
                    trigger.focus();
                    action.open();
                  }}
                >
                  {phoneHeaderOverflow && <>
                    {resolvedShowTopBarQuickAdd && canQuickAdd && <DropdownMenuItem
                      onSelect={() => {
                        pendingHeaderAction.current = { scope: headerActionScope, open: () => {
                          quickAddMenuReturnFocus.current = true;
                          openQuickAddPanel("top_bar");
                        } };
                      }}
                    >
                      <Plus className="h-4 w-4" />
                      Quick Add
                    </DropdownMenuItem>}
                    <ChangelogEntryPointButton
                      presentation="menu-item"
                      onMenuSelect={(open) => { pendingHeaderAction.current = { open, scope: headerActionScope }; }}
                    />
                  </>}
                  {solomonEnabled && <DropdownMenuItem
                    onClick={() => {
                      setSolomonDraft(null);
                      setSolomonOpen(true);
                    }}
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    Ask Solomon
                  </DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <a href="https://forgehq.io/docs" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <BookOpen className="mr-2 h-4 w-4" />
                      Knowledge Base
                      <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a href="https://community.forgehq.io" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <MessagesSquare className="mr-2 h-4 w-4" />
                      Community Forum
                      <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { trackAction({ action: "support_ticket_open", source: "app_shell", metadata: { origin: "mobile_more_menu" } }); navigate("/support/ticket"); }}>
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Submit Support Ticket
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a href="mailto:support@forgehq.io" className="flex items-center">
                      <Mail className="mr-2 h-4 w-4" />
                      Email Support
                    </a>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn("p-0", isMobile && platform.isNative && "hidden")}
                  aria-label="User menu"
                  title="User menu"
                >
                  <Avatar className="h-9 w-9">
                    {avatarUrl ? (
                      <AvatarImage src={avatarUrl} alt={userEmail || "User avatar"} />
                    ) : null}
                    <AvatarFallback className="text-xs">{userInitials}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium break-words" title={user?.email}>
                      {user?.email}
                    </p>
                    {roles.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {roles.map((role) => (
                          <Badge key={role} variant="secondary" className="text-xs capitalize">
                            {role}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {roles.length === 0 && (
                      <p className="text-xs text-muted-foreground">No roles assigned</p>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={() => navigate("/member/profile")}>
                    <User className="mr-2 h-4 w-4" />
                    My account
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/member/preferences")}>
                    <Settings className="mr-2 h-4 w-4" />
                    Preferences
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/member/notifications")}>
                    <Bell className="mr-2 h-4 w-4" />
                    Notifications
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5">
                  <p className="mb-1.5 text-xs font-medium text-muted-foreground">Theme</p>
                  <ThemeToggle />
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings/preferences#starter-task-archive")}>
                  <Check className="mr-2 h-4 w-4" />
                  Starter task archive
                </DropdownMenuItem>
                {isStaff && location.pathname.startsWith("/member") && (
                  <DropdownMenuItem onClick={() => navigate("/today")}>
                    <ArrowRightLeft className="mr-2 h-4 w-4" />
                    Switch to Staff Portal
                  </DropdownMenuItem>
                )}
                {!location.pathname.startsWith("/member") && (
                  <DropdownMenuItem onClick={() => navigate("/member")}>
                    <ArrowRightLeft className="mr-2 h-4 w-4" />
                    Switch to Member Portal
                  </DropdownMenuItem>
                )}
                {/* Platform admins get a discoverable entry point to /admin.
                 * Gate is the same `accessContext.capabilities.isPlatformAdmin`
                 * that AdminRoute uses — visibility and route-access stay
                 * coupled, so it's impossible to show a link that 404s. */}
                {isPlatformAdmin && !location.pathname.startsWith("/admin") && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                      <Shield className="mr-2 h-4 w-4" />
                      Platform Admin
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>}

        {/* Page Content — extra bottom padding on native mobile for bottom nav */}
        <main
          id="main-content"
          tabIndex={-1}
          className={cn(
            // scroll-mt offsets the sticky header so the skip-link target isn't
            // hidden behind it after the jump (2.4.11 Focus Not Obscured).
            "flex-1 min-h-0 overflow-y-auto focus:outline-none",
            !chromeFree && "module-content-area px-3 py-page sm:px-4 md:px-page scroll-pt-4 scroll-mt-16",
            !chromeFree && (isMobile && platform.isNative ? "pb-28" : "pb-14 md:pb-16 lg:pb-20")
          )}>
          {!chromeFree && hasLayoutHeader ? (
            <PageHeader
              title={title ?? ""}
              titleIsShell={titleIsShell}
              subtitle={subtitle}
              hideTitle={hideLayoutTitle}
              actions={titleActions}
              className="mb-4 rounded-none border-0 bg-transparent p-0"
              contextLine={moduleIdentity === "subtle" ? "" : undefined}
            />
          ) : null}
          {children}
        </main>

        {!chromeFree && <footer className={cn("app-layout-chrome print-hidden border-t border-border/60 bg-background px-3 py-2 text-xs text-muted-foreground md:px-6", isMobile && "hidden")}>
          <div className="flex flex-wrap items-center justify-center gap-2 md:justify-end md:gap-3">
            <a href="https://forgehq.io/docs" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
              KB
            </a>
            <span aria-hidden="true">•</span>
            <a href="https://forgehq.io/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
              Privacy
            </a>
            <span aria-hidden="true">•</span>
            <a href="https://forgehq.io/terms" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
              T&amp;C
            </a>
            <span aria-hidden="true">•</span>
            <a href="https://forgehq.io/data-deletion" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
              Data Deletion
            </a>
          </div>
        </footer>}
      </div>

      {/* Network Status Banner for offline mode */}
      <div className="print-hidden">
        <NetworkStatusBanner />
      </div>

      {/* Quick Add Glass Panel */}
      {!chromeFree && <QuickAddPanel
        open={quickAddPanelOpen}
        onCloseAutoFocus={(event) => {
          if (!quickAddMenuReturnFocus.current) return;
          quickAddMenuReturnFocus.current = false;
          const target = [headerActionsTrigger.current, topBarQuickAddTrigger.current].find((element) => {
            if (!element?.isConnected || element.disabled || element.getAttribute("aria-disabled") === "true") return false;
            const rect = element.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0 && getComputedStyle(element).visibility === "visible";
          });
          if (target) { event.preventDefault(); target.focus(); }
        }}
        onOpenChange={setQuickAddPanelOpen}
        onAction={(action) => {
          trackAction({ action: "quick_add_select", source: "app_shell", metadata: { item: action } });
          clearGenieOrigin();
          switch (action) {
            case "add-person":
              openPersonDialog();
              break;
            case "create-request":
              openRequestDialog();
              break;
            case "record-contribution":
              openContributionDialog();
              break;
            case "create-event":
              openEventDialog();
              break;
            case "post-announcement":
              navigate("/announcements/new");
              break;
            case "create-project":
              openProjectDialog();
              break;
            case "create-service":
              openServiceDialog();
              break;
            case "create-message":
              openMessageDialog();
              break;
            case "create-reimbursement":
              openRequestDialog({ type: "reimbursement" });
              break;
          }
        }}
      />}

      {/* Quick Add Dialogs — wrapped in genie context for button-origin animation */}
      <DialogGenieContext.Provider value={genieOrigin}>
        {personDialogLoaded ? (
          <Suspense fallback={null}>
            <PersonFormDialog
              open={personDialogOpen}
              onOpenChange={(open) => { handlePersonDialogChange(open); if (!open) clearGenieOrigin(); }}
              mode="add"
              defaultValues={personDialogDefaults}
            />
          </Suspense>
        ) : null}

        {contributionDialogLoaded ? (
          <Suspense fallback={null}>
            <QuickContributionEntry
              open={contributionDialogOpen}
              onOpenChange={(open) => { setContributionDialogOpen(open); if (!open) clearGenieOrigin(); }}
            />
          </Suspense>
        ) : null}

        {requestDialogLoaded ? (
          <Suspense fallback={null}>
            <GeneralRequestDialog
              open={requestDialogOpen}
              onOpenChange={(open) => { setRequestDialogOpen(open); if (!open) clearGenieOrigin(); }}
              defaultTitle={requestDefaults.title}
              defaultType={requestDefaults.type}
              defaultPersonId={requestDefaults.personId}
              defaultPersonName={requestDefaults.personName}
            />
          </Suspense>
        ) : null}

        {eventDialogLoaded ? (
          <Suspense fallback={null}>
            <EventFormDialog
              open={eventDialogOpen}
              onOpenChange={(open) => { setEventDialogOpen(open); if (!open) clearGenieOrigin(); }}
            />
          </Suspense>
        ) : null}

        {serviceDialogLoaded ? (
          <Suspense fallback={null}>
            <ServiceFormDialog
              open={serviceDialogOpen}
              onOpenChange={(open) => { setServiceDialogOpen(open); if (!open) clearGenieOrigin(); }}
            />
          </Suspense>
        ) : null}

        {projectDialogLoaded ? (
          <Suspense fallback={null}>
            <ProjectFormDialog
              open={projectDialogOpen}
              onOpenChange={(open) => { setProjectDialogOpen(open); if (!open) clearGenieOrigin(); }}
              mode="add"
            />
          </Suspense>
        ) : null}

        {messageDialogLoaded ? (
          <Suspense fallback={null}>
            <ComposeMessageDialog
              open={messageDialogOpen}
              onOpenChange={(open) => { setMessageDialogOpen(open); if (!open) clearGenieOrigin(); }}
              context="Draft outreach with saved templates, merge fields, and email rich text when needed."
            />
          </Suspense>
        ) : null}
      </DialogGenieContext.Provider>

      {!chromeFree && <Dialog open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Keyboard Shortcuts</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground">
            <div className="flex items-center justify-between">
              <span>Open search</span>
              <span className="font-mono text-foreground">⌘K / Ctrl+K</span>
            </div>
            <div className="flex items-center justify-between">
              <span>New contribution</span>
              <span className="font-mono text-foreground">⌘N / Ctrl+N</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Quick person lookup</span>
              <span className="font-mono text-foreground">⌘P / Ctrl+P</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Show shortcuts</span>
              <span className="font-mono text-foreground">⌘/ / Ctrl+/</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Go to People</span>
              <span className="font-mono text-foreground">G then P</span>
            </div>
            <div className="border-t pt-2 mt-1 text-xs font-semibold uppercase tracking-wide text-foreground">
              On the Tasks page
            </div>
            <div className="flex items-center justify-between">
              <span>Move / open focused task</span>
              <span className="font-mono text-foreground">↑ ↓ / Enter</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Select focused / all tasks</span>
              <span className="font-mono text-foreground">x / ⌘A</span>
            </div>
            <div className="flex items-center justify-between">
              <span>New task / toggle done</span>
              <span className="font-mono text-foreground">c / e</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Clear focus / selection</span>
              <span className="font-mono text-foreground">Esc</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>}

      <SolomonPanel
        open={solomonOpen}
        onOpenChange={(nextOpen) => {
          setSolomonOpen(nextOpen);
          if (!nextOpen) {
            setSolomonDraft(null);
          }
        }}
        draftPrompt={solomonDraft}
        onDraftPromptConsumed={() => setSolomonDraft(null)}
      />

      {/* Native mobile bottom navigation */}
      {!chromeFree && <MobileBottomNav
        canQuickAdd={canQuickAdd}
        onQuickAdd={() => openQuickAddPanel("mobile_bottom_nav")}
        onSearch={() => openCommandPalette("mobile_bottom_nav")}
        onMoreMenu={() => {
          // Open the sidebar drawer on mobile when "More" is tapped
          setIsMobileOpen(true);
        }}
      />}
      </div>
      </TopbarBreadcrumbClaimContext.Provider>
      </TopbarBreadcrumbSlotContext.Provider>
      </ModuleScopeContext.Provider>
    </QuickAddProvider>
  );
}

export function AppLayout(props: AppLayoutProps) {
  const { organizationId } = useOrganization();
  const { user } = useAuth();
  const { tenantContext } = useTenantContext();
  // Loading may preserve drafts within one session, never across a tenant/user
  // or support-session boundary. Rekey form content so late callbacks address only
  // the old instance, while browser install and sidebar preferences stay mounted.
  const sessionKey = JSON.stringify([organizationId, user?.id, tenantContext?.supportSessionId]);
  return (
    <>
      <SidebarProvider>
        <AppLayoutTitleContext.Provider value={Boolean(props.mode !== "chrome-free" && props.title && !props.hideTitle)}>
          <AppLayoutContent key={sessionKey} {...props} />
        </AppLayoutTitleContext.Provider>
      </SidebarProvider>
      {/* Browser install events belong to the browser session, not the tenant. */}
      <InstallPrompt visible={props.mode !== "chrome-free"} />
    </>
  );
}
